import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  AttentionMetricKey,
  WorkspaceAttentionItem,
  WorkspaceAttentionMetric,
} from '../../domain/safetyCases/workspaceAttention';
import {
  attentionItemDisplayLabel,
  buildAttentionSummaryStrip,
  formatAttentionOpenAgeLabel,
  sortWorkspaceAttentionItems,
} from '../../domain/safetyCases/workspaceAttention';
import {
  canonicalWorkspaceStatusLabel,
  isEmptyRegisterValue,
} from '../../domain/safetyCases/workspaceStatusDimensions';
import { WorkspaceSavedViewsBar } from './WorkspaceSavedViewsBar';
import {
  ATTENTION_EMPTY,
  attentionObjectLabel,
} from '../../domain/safetyCases/safetyCasesWorkspacePresentationCopy';

function formatUpdated(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

export function WorkspaceAttentionPanel({
  metrics,
  items,
  activeFilter,
  loading,
  error,
  onToggleFilter,
  onRetry,
  onAction,
}: {
  metrics: WorkspaceAttentionMetric[];
  items: WorkspaceAttentionItem[];
  activeFilter: AttentionMetricKey | null;
  loading: boolean;
  error?: string | null;
  onToggleFilter: (key: AttentionMetricKey) => void;
  onRetry?: () => void;
  onAction: (item: WorkspaceAttentionItem) => void;
}) {
  const sortedItems = useMemo(() => sortWorkspaceAttentionItems(items), [items]);
  const summary = useMemo(
    () => buildAttentionSummaryStrip(metrics, sortedItems),
    [metrics, sortedItems],
  );
  const showDueColumn = useMemo(
    () => sortedItems.some((item) => Boolean(item.dueAt) && !isEmptyRegisterValue(item.dueAt)),
    [sortedItems],
  );

  return (
    <Stack spacing={1.25} data-testid="workspace-attention-panel">
      <WorkspaceSavedViewsBar tab="attention" />

      <Box
        data-testid="attention-summary-strip"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          px: 1.25,
          py: 0.85,
        }}
      >
        {/* Alias node for legacy dashboard tests expecting safety-reports-kpi-row */}
        <Box data-testid="safety-reports-kpi-row" sx={{ display: 'contents' }}>
        {loading ? (
          <Skeleton width="60%" height={28} />
        ) : (
          summary.segments.map((segment, index) => {
            const selected = Boolean(segment.filterKey) && activeFilter === segment.filterKey;
            const clickable = Boolean(segment.filterKey);
            return (
              <React.Fragment key={segment.key}>
                {index > 0 ? (
                  <Typography variant="body2" color="text.secondary" aria-hidden>
                    ·
                  </Typography>
                ) : null}
                {clickable ? (
                  <Button
                    size="small"
                    variant={selected ? 'contained' : 'text'}
                    aria-pressed={selected}
                    data-testid={`attention-metric-${segment.filterKey}`}
                    onClick={() => segment.filterKey && onToggleFilter(segment.filterKey)}
                    sx={{ textTransform: 'none', fontWeight: 700, minHeight: 30, px: 1 }}
                  >
                    {segment.label}
                  </Button>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700 }}
                    data-testid="attention-summary-oldest-open"
                  >
                    {segment.label}
                  </Typography>
                )}
              </React.Fragment>
            );
          })
        )}
        </Box>
      </Box>
      {activeFilter ? (
        <Typography variant="body2" color="text.secondary" data-testid="attention-filter-chip">
          Filtered by {metrics.find((metric) => metric.key === activeFilter)?.label || activeFilter}
          {' · '}
          <Button size="small" onClick={() => onToggleFilter(activeFilter)} sx={{ textTransform: 'none', minWidth: 0, p: 0 }}>
            Clear filter
          </Button>
        </Typography>
      ) : null}

      {error ? (
        <Alert
          severity="error"
          action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
        >
          Attention data failed to load. Counts may be incomplete. {error}
        </Alert>
      ) : null}

      {loading ? (
        <Stack spacing={1} data-testid="attention-queue-loading">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} variant="rounded" height={52} />
          ))}
        </Stack>
      ) : sortedItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="attention-queue-empty" sx={{ py: 1.5 }}>
          {ATTENTION_EMPTY}
        </Typography>
      ) : (
        <TableContainer data-testid="attention-queue-table" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small" aria-label="Attention queue">
            <TableHead>
              <TableRow>
                <TableCell>Priority</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Evidence</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Work state</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Updated</TableCell>
                {showDueColumn ? <TableCell>Due</TableCell> : null}
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((item) => {
                const ageLabel = formatAttentionOpenAgeLabel(item.updatedAt);
                const itemDisplay = attentionItemDisplayLabel(item);
                return (
                  <TableRow
                    key={item.id}
                    data-testid={`attention-row-${item.id}`}
                    hover
                    sx={{
                      height: 56,
                      borderLeft: item.isOverdue ? 4 : 0,
                      borderLeftColor: item.isOverdue ? 'error.main' : 'transparent',
                      '& td': { py: 0.75, borderBottomColor: 'divider' },
                    }}
                  >
                    <TableCell sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <span>{item.priority}</span>
                        {item.isOverdue ? <Chip size="small" color="error" label="Overdue" /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.25 }}
                        title={itemDisplay.canonicalTitle || undefined}
                      >
                        {itemDisplay.primary}
                      </Typography>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid={`attention-object-${item.id}`}
                        >
                          {attentionObjectLabel(item.itemType)}
                        </Typography>
                        {ageLabel ? (
                          <Chip
                            size="small"
                            color={item.isOverdue || (attentionAgeIsStale(ageLabel)) ? 'warning' : 'default'}
                            variant="outlined"
                            label={ageLabel}
                            data-testid={`attention-age-${item.id}`}
                          />
                        ) : null}
                        {item.isUnowned ? <Chip size="small" variant="outlined" label="Unowned" /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography
                        variant="body2"
                        title={item.evidence}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.3,
                        }}
                      >
                        {item.evidence}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.location}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {canonicalWorkspaceStatusLabel(item.workState, 'work_state')}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.owner}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatUpdated(item.updatedAt)}</TableCell>
                    {showDueColumn ? (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {item.dueAt ? formatUpdated(item.dueAt) : '—'}
                      </TableCell>
                    ) : null}
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onAction(item)}
                        data-testid={`attention-cta-${item.id}`}
                        sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                      >
                        {item.actionLabel}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

function attentionAgeIsStale(ageLabel: string): boolean {
  const match = ageLabel.match(/(\d+)d/);
  if (!match) return false;
  return Number(match[1]) >= 7;
}
